import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_PhotoTranslator = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/photo-translator',
			name: 'Photo Translate — AI Image Translator Online | Lift',
			description:
				'Translate text in photos and pictures instantly with AI. No watermark. Upload a pic, translate image text across 100+ languages, and download your result in seconds. Try now!',
			url: 'https://liftapp.ai/photo-translator',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/photo-translator#product' },
				{ '@id': 'https://liftapp.ai/photo-translator#howto' },
				{ '@id': 'https://liftapp.ai/photo-translator#benefits' },
				{ '@id': 'https://liftapp.ai/photo-translator#faq' },
				{ '@id': 'https://liftapp.ai/photo-translator#why-lift' },
				{ '@id': 'https://liftapp.ai/photo-translator#explore-tools' },
				{ '@id': 'https://liftapp.ai/photo-translator#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/photo-translator#product',
			name: 'AI Image Translator Online',
			description:
				'Translate text in photos and pictures instantly with AI. Upload an image, pick a target language, and get accurate image text translation in seconds — no watermark.',
			url: 'https://liftapp.ai/photo-translator',
			fileFormat: ['image/jpeg', 'image/png', 'image/webp'],
			memoryRequirements: 'Max file size: 20MB',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/photo-translator#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload your image',
					text: "Choose a photo from your device or take one using Lift's app",
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Pick your target language',
					text: 'Browse the most popular languages or scroll the full list to find the right one',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Let AI translate it',
					text: 'Click"Translate Image" and let AI detect and translate all text in the photo while preserving the original layout, fonts, and visual design',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download or regenerate',
					text: 'Save your translated image in high resolution, regenerate it for a refined result, or start fresh with a new photo and a different language',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/photo-translator#benefits',
			name: 'Who can benefit from Image Translate AI?',
			description:
				"Lift's AI image translator helps travelers, students, sellers, creators, and businesses translate text in photos while keeping the original design and context.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Travelers can instantly translate signs, menus, tickets, or directions captured on their phone while keeping full visual context intact',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Students can translate text from scanned textbooks, slides, or research images without retyping a word',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'E-commerce sellers can translate product labels, packaging, and listing images in seconds without touching the original design',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Content creators can repurpose infographics, memes, and educational visuals from any language without rebuilding the layout',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Businesses can localize ads, banners, and marketing visuals for international markets without recreating assets from scratch',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'Benefits from AI Image Translate',
				description:
					'Translate text in images with Lift AI Image Translator while keeping the original layout and design.',
			},
		},
		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/photo-translator#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What languages does the image translator support?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Lift's AI image translator supports 130+ languages including English, Chinese, Japanese, Korean, Spanish, French, German, Portuguese, and many more. After uploading your photo, you'll see a curated list of the most popular options followed by the complete list of all available languages — so you can translate image text from and to virtually any language you need.",
					},
				},
				{
					'@type': 'Question',
					name: 'Does the tool handle images with complex or multi-layered designs?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. The AI is built to detect and translate text even in pictures with intricate backgrounds, multiple text areas, or layered visual elements. It preserves the original layout so the translated result looks as close to the source image as possible.',
					},
				},
				{
					'@type': 'Question',
					name: 'Does translating text inside an image reduce its quality?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "No. Lift's image translator is designed to maintain original image quality throughout the process. The output preserves the resolution, visual style, and layout of your source photo — only the text changes.",
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the results without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Absolutely. All images generated or edited in Lift can be exported without watermark when you're on the Pro plan. Download them in high-res PNG or JPG — ready for social media, print, ads, or e-commerce.",
					},
				},
			],
		},
		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/photo-translator#why-lift',
			name: "Why Lift's AI Image Translator stands out",
			description:
				"Lift's AI image translator delivers high-quality translations, intelligent rendering, cross-platform access, and an affordable workflow for fast localization.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'Lift AI text from image translator',
				description:
					'Lift AI Image Translator benefits — high quality, intelligent rendering, cross-platform access, and affordability.',
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/photo-translator#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},
		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/photo-translator#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},
		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Photo Translator',
					item: 'https://liftapp.ai/photo-translator',
				},
			],
		},
	],
};
